import React, { useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { LifeBuoy, Plus, ExternalLink } from "lucide-react";
import { api, apiErrorText } from "@/lib/api";
import {
  VLKPageHeader, VLKCard, VLKCardHeader, VLKCardTitle, VLKCardContent,
  VLKDataTable, VLKStatusBadge, VLKRiskMeter, VLKButton, VLKSelect,
  VLKAlertDialog, VLKInput, VLKTextarea, VLKLabel, VLKKPICard, toast,
} from "@/components/vlk";
import { IDS } from "@/constants/testIds";
import { cn } from "@/lib/utils";

const STATE_TONE = {
  SUBMITTED:            "pending",
  AWAITING_ATTESTATION: "pending",
  AWAITING_APPROVAL:    "pending",
  APPROVED:             "verified",
  REACTIVATED:          "active",
  REJECTED:             "rejected",
};

export default function Recovery() {
  const qc = useQueryClient();
  const navigate = useNavigate();
  const [stateFilter, setStateFilter] = useState("");
  const [openNew, setOpenNew] = useState(false);
  const [vlkId, setVlkId] = useState("");
  const [reason, setReason] = useState("");
  const [contactEmail, setContactEmail] = useState("");
  const [contactPhone, setContactPhone] = useState("");
  const [busy, setBusy] = useState(false);

  const { data } = useQuery({
    queryKey: ["recovery-requests", stateFilter],
    queryFn: () => api.get("/recovery/requests", { params: { state: stateFilter || undefined, limit: 200 } }).then(r => r.data),
  });

  const kpis = useMemo(() => {
    const rows = data?.items || [];
    const count = (s) => rows.filter(r => r.state === s).length;
    return {
      awaiting: count("AWAITING_ATTESTATION") + count("AWAITING_APPROVAL") + count("SUBMITTED"),
      approved: count("APPROVED"),
      reactivated: count("REACTIVATED"),
      rejected: count("REJECTED"),
    };
  }, [data]);

  const submit = async () => {
    setBusy(true);
    try {
      const { data: created } = await api.post("/recovery/request", {
        vlk_id: vlkId.trim(),
        reason: reason.trim(),
        contact_email: contactEmail.trim(),
        contact_phone: contactPhone.trim() || undefined,
      });
      toast.success("Recovery request submitted", { description: created.request_id });
      setOpenNew(false); setVlkId(""); setReason(""); setContactEmail(""); setContactPhone("");
      qc.invalidateQueries({ queryKey: ["recovery-requests"] });
      navigate(`/recovery/${created.request_id}`);
    } catch (e) { toast.error("Failed to submit", { description: apiErrorText(e) }); }
    finally { setBusy(false); }
  };

  const columns = [
    { key: "request_id", header: "Request", mono: true, render: (r) => (
      <button
        onClick={() => navigate(`/recovery/${r.request_id}`)}
        className="font-mono text-xs inline-flex items-center gap-1.5 hover:text-foreground text-muted-foreground group"
        data-testid={IDS.recoveryOpen(r.request_id)}
      >{r.request_id}<ExternalLink className="h-3 w-3 opacity-40 group-hover:opacity-100" /></button>
    )},
    { key: "identity_name", header: "Identity", render: (r) => (
      <div>
        <div className="font-medium">{r.identity_name}</div>
        <div className="font-mono text-[10px] text-muted-foreground">{r.vlk_id}</div>
      </div>
    )},
    { key: "state", header: "State", render: (r) =>
      <VLKStatusBadge status={STATE_TONE[r.state] || "default"}>{r.state.replace(/_/g, " ")}</VLKStatusBadge>
    },
    { key: "risk_level", header: "Risk", render: (r) => <VLKRiskMeter level={r.risk_level} /> },
    { key: "attestation", header: "Attestation", render: (r) => r.attestation
        ? <span className="text-xs font-mono text-muted-foreground">{r.attestation.institution_name}</span>
        : <span className="text-xs text-muted-foreground">—</span>
    },
    { key: "submitted_at", header: "Submitted", mono: true, render: (r) => new Date(r.submitted_at).toLocaleDateString() },
  ];

  return (
    <div>
      <VLKPageHeader
        title="Recovery workflow"
        description="Multi-party identity restoration with immutable audit trail."
        actions={
          <VLKButton variant="primary" onClick={() => setOpenNew(true)} data-testid={IDS.recoveryNew}>
            <Plus className="h-4 w-4" />New request
          </VLKButton>
        }
      />

      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <VLKKPICard label="In-flight" value={kpis.awaiting} icon={LifeBuoy} tone="warning" testId={IDS.recoveryKpiInflight} />
        <VLKKPICard label="Approved · awaiting reactivation" value={kpis.approved} icon={LifeBuoy} tone="info" />
        <VLKKPICard label="Reactivated" value={kpis.reactivated} icon={LifeBuoy} tone="success" />
        <VLKKPICard label="Rejected" value={kpis.rejected} icon={LifeBuoy} tone="danger" />
      </div>

      <VLKCard className="mb-4">
        <div className="p-3 flex items-center gap-2 border-b border-border">
          <VLKSelect value={stateFilter} onChange={(e) => setStateFilter(e.target.value)} className="max-w-[240px]" data-testid={IDS.recoveryStateFilter}>
            <option value="">All states</option>
            <option value="AWAITING_ATTESTATION">Awaiting attestation</option>
            <option value="AWAITING_APPROVAL">Awaiting approval</option>
            <option value="APPROVED">Approved · awaiting reactivation</option>
            <option value="REACTIVATED">Reactivated</option>
            <option value="REJECTED">Rejected</option>
          </VLKSelect>
        </div>
        <VLKDataTable
          columns={columns}
          rows={data?.items || []}
          rowKey={(r) => r.request_id}
          empty="No recovery requests match filters."
        />
      </VLKCard>

      <VLKAlertDialog
        open={openNew}
        onOpenChange={setOpenNew}
        title="New recovery request"
        actions={
          <>
            <VLKButton variant="ghost" onClick={() => setOpenNew(false)}>Cancel</VLKButton>
            <VLKButton
              variant="primary"
              onClick={submit}
              loading={busy}
              disabled={!vlkId || reason.length < 10 || !contactEmail}
              data-testid={IDS.recoverySubmit}
            >Submit</VLKButton>
          </>
        }
      >
        <div className="space-y-3">
          <div className="text-xs text-muted-foreground">
            Submit on behalf of a citizen. The request enters <span className="font-mono text-foreground">AWAITING_ATTESTATION</span> and notifies institution + compliance officers.
          </div>
          <div className="space-y-1.5">
            <VLKLabel>VLK_ID</VLKLabel>
            <VLKInput value={vlkId} onChange={(e) => setVlkId(e.target.value.toUpperCase())} placeholder="VLK_XXXXXXX" className="font-mono" data-testid={IDS.recoveryVlkInput} />
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Contact email</VLKLabel>
            <VLKInput type="email" value={contactEmail} onChange={(e) => setContactEmail(e.target.value)} placeholder="citizen@example.com" data-testid={IDS.recoveryEmailInput} />
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Contact phone (optional)</VLKLabel>
            <VLKInput value={contactPhone} onChange={(e) => setContactPhone(e.target.value)} placeholder="+234..." className="font-mono" />
          </div>
          <div className="space-y-1.5">
            <VLKLabel>Reason (min 10 chars)</VLKLabel>
            <VLKTextarea value={reason} onChange={(e) => setReason(e.target.value)} rows={4} placeholder="Describe why recovery is being requested…" data-testid={IDS.recoveryReasonInput} />
          </div>
        </div>
      </VLKAlertDialog>
    </div>
  );
}
